/**
 * The vendor's home screen — same narrow, event-driven shape OwnerHome.tsx
 * aims for, with one deliberate difference worth flagging rather than
 * copying silently: OwnerHome's own hero queries `owner-disbursements`
 * filtered to `status = 'TRANSFERRED'`, but `owner_disbursements_owner_select`
 * (rls-policies.sql) only ever reveals `PAID`/`SETTLED_EXTERNALLY` rows to
 * an owner — the same restriction `vendor_disbursements_vendor_select`
 * deliberately mirrors here. That means OwnerHome's "in transit" count is
 * unreachable for a real owner today; rather than reproduce that same dead
 * query for vendors, this hero counts `PAID` payouts instead — a real,
 * RLS-visible signal — and the open-work-orders count stands alone.
 *
 * No document list here, unlike OwnerHome — vendors have no document
 * access at all (out of scope for this pass, per CLAUDE.md's Vendor
 * disbursements section).
 */

import { useList } from '@refinedev/core';
import { useSession } from '../../../lib/session.js';
import { Bento, BentoCell, HeroFigure } from '../../../components/Bento.js';

interface PaidDisbursementRow {
  id: string;
  amount: string;
  status: string;
}

interface OpenWorkOrderRow {
  id: string;
  status: string;
}

export function VendorHome() {
  const { state } = useSession();

  const { data: disbursements } = useList<PaidDisbursementRow>({
    resource: 'vendor-disbursements',
    filters: [{ field: 'status', operator: 'eq', value: 'PAID' }],
    pagination: { mode: 'off' },
  });

  const { data: workOrders } = useList<OpenWorkOrderRow>({
    resource: 'work-orders',
    pagination: { mode: 'off' },
  });

  if (state.status !== 'signed-in') return null;

  const paid = disbursements?.data ?? [];
  const open = (workOrders?.data ?? []).filter(
    (row) => row.status !== 'COMPLETED' && row.status !== 'CANCELED',
  );

  return (
    <section>
      <h1>Dashboard</h1>

      <Bento>
        <BentoCell span="full">
          <HeroFigure
            label="Payouts received"
            value={paid.length}
            caption={
              paid.length === 1
                ? 'One payout has reached your bank.'
                : `${String(paid.length)} payouts have reached your bank.`
            }
          />
        </BentoCell>

        <BentoCell span="full" title="Open work orders">
          <p className="bento__hint">
            {open.length === 0
              ? 'Nothing open right now.'
              : `${String(open.length)} job(s) assigned to you are still open.`}
          </p>
        </BentoCell>
      </Bento>
    </section>
  );
}
